<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { BookOpen, ChevronDown, LayoutDashboard, LogOut, UserRound } from '@lucide/vue'
import { useSessionStore } from '@/core/auth/session'
import { siteConfig } from './site-config'

const route = useRoute()
const session = useSessionStore()
const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const expanded = ref(false)
const displayName = computed(() => session.identity?.displayName || session.identity?.username || '')

function close(): void {
  expanded.value = false
}

function leave(event: PointerEvent): void {
  if (event.pointerType === 'mouse' && !root.value?.contains(document.activeElement)) close()
}

function focusOut(event: FocusEvent): void {
  if (!(event.relatedTarget instanceof Node) || !root.value?.contains(event.relatedTarget)) close()
}

function outside(event: PointerEvent): void {
  if (event.target instanceof Node && !root.value?.contains(event.target)) close()
}

async function keydown(event: KeyboardEvent): Promise<void> {
  if (event.key === 'Escape' && expanded.value) {
    event.preventDefault()
    event.stopPropagation()
    close()
    trigger.value?.focus()
  } else if (event.target === trigger.value && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault()
    expanded.value = true
    await nextTick()
    const items = root.value?.querySelectorAll<HTMLElement>('[data-account-item]')
    const index = event.key === 'ArrowUp' ? (items?.length ?? 1) - 1 : 0
    items?.[index]?.focus()
  }
}

watch(() => route.fullPath, close)
onMounted(() => document.addEventListener('pointerdown', outside))
onBeforeUnmount(() => document.removeEventListener('pointerdown', outside))
</script>

<template>
  <div
    ref="root"
    class="account-menu"
    @pointerenter="($event.pointerType === 'mouse') && (expanded = true)"
    @pointerleave="leave"
    @focusout="focusOut"
    @keydown="keydown"
  >
    <button
      ref="trigger"
      type="button"
      class="account-menu__trigger"
      :aria-label="`${displayName}的账户菜单`"
      :aria-expanded="expanded"
      aria-controls="account-menu-panel"
      @click="expanded = !expanded"
    >
      <span class="account-menu__avatar" aria-hidden="true">{{ displayName.slice(0, 1) }}</span>
      <span class="account-menu__name">{{ displayName }}</span>
      <ChevronDown :size="14" class="account-menu__chevron" aria-hidden="true" />
    </button>
    <div v-if="expanded" id="account-menu-panel" class="account-menu__panel">
      <nav aria-label="账户菜单" class="account-menu__items">
        <RouterLink to="/me" class="account-menu__item" data-account-item @click="close">
          <BookOpen :size="17" aria-hidden="true" />我的学习
        </RouterLink>
        <RouterLink to="/me/profile" class="account-menu__item" data-account-item @click="close">
          <UserRound :size="17" aria-hidden="true" />个人中心
        </RouterLink>
        <a
          v-if="session.identity?.capabilities.admin === true"
          :href="siteConfig.adminEntryUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="account-menu__item"
          data-account-item
          @click="close"
        >
          <LayoutDashboard :size="17" aria-hidden="true" />管理入口
        </a>
        <div class="account-menu__divider" />
        <button type="button" class="account-menu__item" data-account-item @click="close(); session.logout()">
          <LogOut :size="17" aria-hidden="true" />退出
        </button>
      </nav>
    </div>
  </div>
</template>

<style scoped>
.account-menu { position: relative; }
.account-menu__trigger { display: flex; align-items: center; gap: 9px; min-height: 40px; padding: 5px 8px; border-radius: var(--pf-radius-control); }
.account-menu__trigger:hover, .account-menu__trigger[aria-expanded="true"] { background: var(--pf-surface-muted); }
.account-menu__avatar { display: grid; place-items: center; flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; background: var(--pf-accent-soft); color: var(--pf-accent); font-size: 13px; }
.account-menu__name { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.account-menu__chevron { color: var(--pf-text-tertiary); }
.account-menu__panel { position: absolute; z-index: 40; top: 100%; right: 0; width: 172px; padding-top: 8px; }
.account-menu__items { padding: 6px; border: 1px solid var(--pf-border); border-radius: var(--pf-radius-card); background: var(--pf-surface); box-shadow: var(--pf-shadow-hover); }
.account-menu__item { display: flex; align-items: center; gap: 10px; box-sizing: border-box; width: 100%; min-height: 40px; padding: 9px 12px; border-radius: var(--pf-radius-control); color: var(--pf-text-secondary); font-size: 13px; text-align: left; }
.account-menu__item:hover, .account-menu__item:focus-visible { background: var(--pf-accent-soft); color: var(--pf-accent); }
.account-menu__divider { height: 1px; margin: 6px 4px; background: var(--pf-border); }
@media (max-width: 767px) {
  .account-menu__name { display: none; }
  .account-menu__trigger { gap: 5px; padding-inline: 5px; }
}
</style>
